// ============================================================================
// Delivery Control Tower — summary dashboard.
//
// Two tabs, split by board_lane (the same first/last-mile split LogisticsOrderListView uses):
//   - Last Mile: an overview strip (Total Orders / Total Shipments / At Risk / Breached across
//     every lane) plus per-lane cards grouped into Standard / Express / Outstation rows. Every
//     overview and lane figure is clickable, opening a drawer listing the real rows behind it.
//   - First Mile: three stacked sections — GRN (by warehouse), Pick List (by warehouse) and
//     Transfers. GRN and Pick List each render as blocks or a per-warehouse table and drill down
//     through their own escalation drawers; Pick List's "picked" tally drills into
//     FirstMileDrilldownDrawer (/first-mile-receives/, kind=picklist).
// A shared date / owner filter bar above the tabs scopes whichever board is active.
// See types.ts / control_tower.api.ts.
// ============================================================================
import { ReloadOutlined, RadarChartOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, DatePicker, Divider, Row, Select, Statistic, Tabs, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./index.module.scss";
import { LANE_GROUPS, LANE_LABEL } from "./lane_meta";
import { listTeamMembers, TeamMember } from "../../oms/logistics/logistics.api";
import {
  ControlTowerBoardLane,
  ControlTowerSummaryResponse,
  FirstMileEscalationsResponse,
  FirstMilePicklistEscalationsResponse,
  FirstMileSummaryResponse,
  getControlTowerSummary,
  getFirstMileEscalations,
  getFirstMilePicklistEscalations,
  getFirstMileSummary,
  getTeamMetrics,
  TeamMetricMember,
} from "./control_tower.api";
import { DrilldownRequest, FirstMileDrilldownRequest, LastMileLaneKey, MetricKey } from "./types";
import LaneCard from "./components/lane_card";
import { reasonBreakdown } from "./components/sla_reasons";
import FirstMileDrilldownDrawer from "./components/first_mile_drilldown_drawer";
import FirstMileGrnSection from "./components/first_mile_grn_section";
import FirstMilePicklistSection from "./components/first_mile_picklist_section";
import FirstMileEscalationDrawer, {
  EscalationDrilldownRequest,
} from "./components/first_mile_escalation_drawer";
import FirstMilePicklistEscalationDrawer, {
  PicklistDrilldownRequest,
} from "./components/first_mile_picklist_escalation_drawer";
import TransferOrdersSection from "./components/transfer_orders_section";
import TransferOrdersDrawer, {
  TransferDrilldownRequest,
} from "./components/transfer_orders_drawer";
import ShipmentListDrawer from "./components/shipment_list_drawer";
import TeamMetricsSection, { TeamMetricClick } from "./components/team_metrics_section";
import TeamEscalationDrawer, {
  TeamEscalationRequest,
} from "./components/team_escalation_drawer";

const { RangePicker } = DatePicker;

// Default scope: just today.
const DEFAULT_RANGE: [Dayjs, Dayjs] = [dayjs(), dayjs()];

// Shared look for the First Mile section dividers (GRN / Picklist / end) — a bold-ish grey rule with
// an uppercase label, so the tab reads as three stacked sections.
const SECTION_DIVIDER_STYLE: React.CSSProperties = {
  margin: "12px 0 20px",
  borderColor: "#8c8c8c",
  borderBlockStartWidth: 2,
  color: "#595959",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  fontSize: 13,
};

// The delivery crew — every ACTIVE org member whose role (UserOrganisation.user_permission) is
// this. Read from the org roster at request time, so a joiner or leaver shows up the moment their
// role is set, with no code change here. The endpoints take a comma-separated list, so adding
// `delivery_manager` later is a one-word edit.
//
// ONE constant drives BOTH the "Assigned to" dropdown and the Team Metric table: the filter you can
// pick from and the people you are shown have to be the same set, or the board contradicts itself.
// The trade-off is deliberate — the logistics board's own Owner control is not role-filtered, so a
// truck hand-assigned to somebody outside these roles is counted by no row here, and the table does
// not sum to Total Shipments.
const ASSIGNEE_ROLES = "delivery";

const EMPTY_LANE_COUNTS = { total_orders: 0, total_shipments: 0, total_at_risk: 0, total_breached: 0 };

const ControlTower: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(DEFAULT_RANGE);
  const [assignedTo, setAssignedTo] = useState<string | undefined>(undefined); // undefined = "All"
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [summary, setSummary] = useState<ControlTowerSummaryResponse | null>(null);
  const [firstMileSummary, setFirstMileSummary] = useState<FirstMileSummaryResponse | null>(null);
  const [firstMileEscalations, setFirstMileEscalations] = useState<FirstMileEscalationsResponse | null>(null);
  const [escalationDrilldown, setEscalationDrilldown] = useState<EscalationDrilldownRequest | null>(null);
  const [picklistEscalations, setPicklistEscalations] = useState<FirstMilePicklistEscalationsResponse | null>(null);
  const [picklistEscalationDrilldown, setPicklistEscalationDrilldown] =
    useState<PicklistDrilldownRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownRequest | null>(null);
  const [firstMileDrilldown, setFirstMileDrilldown] = useState<FirstMileDrilldownRequest | null>(null);
  const [transferDrilldown, setTransferDrilldown] = useState<TransferDrilldownRequest | null>(null);
  // Per-owner performance for the ACTIVE board. Its own slot per lane: the same person carries
  // different numbers on each, so switching tabs must not show the other lane's figures.
  const [teamMetrics, setTeamMetrics] = useState<Record<ControlTowerBoardLane, TeamMetricMember[]>>({
    first_mile: [],
    last_mile: [],
  });
  const [teamEscalation, setTeamEscalation] = useState<TeamEscalationRequest | null>(null);
  const [boardLane, setBoardLane] = useState<ControlTowerBoardLane>("last_mile"); // active tab

  // Neutral names: the same picked range is sent as delivery_from/_to (Last Mile) or
  // dispatch_from/_to (First Mile), depending on the active board.
  const dateFrom = dateRange[0].format("YYYY-MM-DD");
  const dateTo = dateRange[1].format("YYYY-MM-DD");

  useEffect(() => {
    listTeamMembers({ role: ASSIGNEE_ROLES }).then(setTeamMembers);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    // The two boards differ in both response shape (own state slot) and date dimension: Last Mile
    // scopes by delivery date, First Mile by dispatch date (dispatch_from/_to).
    if (boardLane === "first_mile") {
      // All three share the dispatch window AND the owner filter, so every First Mile figure moves
      // together when an owner is picked. (The one exception, documented server-side: the RAISED
      // procurement escalations are team-wide, since an escalation has no last-mile owner.)
      const scope = { dispatch_from: dateFrom, dispatch_to: dateTo, assigned_to: assignedTo };
      const [res, esc, pickEsc, team] = await Promise.all([
        getFirstMileSummary(scope),
        getFirstMileEscalations(scope),
        getFirstMilePicklistEscalations(scope),
        getTeamMetrics({ ...scope, board_lane: "first_mile", role: ASSIGNEE_ROLES }),
      ]);
      if (res) {
        setFirstMileSummary(res);
        setFirstMileEscalations(esc ?? null);
        setPicklistEscalations(pickEsc ?? null);
        setTeamMetrics((prev) => ({ ...prev, first_mile: team?.members ?? [] }));
        setError(false);
        setUpdatedAt(Date.now());
      } else {
        setError(true);
      }
    } else {
      const scope = { delivery_from: dateFrom, delivery_to: dateTo, assigned_to: assignedTo };
      const [res, team] = await Promise.all([
        getControlTowerSummary({ ...scope, board_lane: boardLane }),
        getTeamMetrics({ ...scope, board_lane: "last_mile", role: ASSIGNEE_ROLES }),
      ]);
      if (res) {
        setSummary(res);
        setTeamMetrics((prev) => ({ ...prev, last_mile: team?.members ?? [] }));
        setError(false);
        setUpdatedAt(Date.now());
      } else {
        setError(true);
      }
    }
    setLoading(false);
  }, [dateFrom, dateTo, assignedTo, boardLane]);

  useEffect(() => {
    load();
  }, [load]);

  const onMetricClick = (lane: LastMileLaneKey | "all", metric: MetricKey) => setDrilldown({ lane, metric });

  // A Team Metric figure opens the rows behind it, scoped to that person rather than to the page's
  // "Assigned to" filter. The four shipment counts reuse the shared shipment drawer (`owner` is what
  // re-scopes and re-titles it); Escalations has its own list, since an escalation is an event on a
  // stage, not a booking.
  const onTeamDrill = (click: TeamMetricClick) => {
    const owner = { id: click.member.user_id, name: click.member.name };
    if (click.kind === "shipments") {
      setDrilldown({ lane: "all", metric: click.metric, owner });
    } else if (click.kind === "grn") {
      // The GRN leg is a first-mile leg, not a booking — so it uses the First Mile leg list the
      // GRN / Pick List sections already drill into, scoped to this person's inbounds.
      setFirstMileDrilldown({ kind: "receive", metric: "grn_marked", owner });
    } else {
      setTeamEscalation({ userId: owner.id, name: owner.name, reason: click.reason });
    }
  };

  const unclassifiedCount = summary?.lanes?.unclassified?.total_shipments ?? 0;

  // Shared across both tabs — the same date / owner scope drives whichever board is active.
  const filterBar = (
    <div className={styles.filterBar}>
      <div>
        <div className={`${styles.filterLabel} ${styles.filterLabelStrong}`}>
          {boardLane === "first_mile" ? "Dispatch date" : "Delivery date"}
        </div>
        <RangePicker
          allowClear={false}
          format="DD MMM YYYY"
          value={dateRange}
          onChange={(v) => v && v[0] && v[1] && setDateRange([v[0], v[1]])}
          presets={[
            { label: "Today", value: [dayjs(), dayjs()] },
            { label: "Yesterday", value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")] },
            { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
            { label: "This month", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
          ]}
        />
      </div>
      <div>
        <div className={styles.filterLabel}>Assigned to</div>
        <Select
          allowClear
          showSearch
          placeholder="All"
          style={{ width: 180 }}
          value={assignedTo}
          onChange={(v) => setAssignedTo(v ?? undefined)}
          onClear={() => setAssignedTo(undefined)}
          options={teamMembers.map((m) => ({ value: m.value, label: m.label }))}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>
    </div>
  );

  // The totals across the whole active board — every figure clickable, opening the drill-down
  // drawer scoped to "all" lanes. Rendered in both tabs (Last Mile also shows per-lane cards below).
  const overviewStrip = (marginBottom: number) => (
    <Row gutter={16} style={{ marginBottom }}>
      <Col xs={24} sm={12} lg={6}>
        <Card
          size="small"
          loading={loading}
          className={`${styles.overallCard} ${styles.overallCardInfo}`}
          onClick={() => onMetricClick("all", "total_orders")}
        >
          <Statistic
            title="Total Orders"
            value={summary?.total_orders ?? 0}
            valueStyle={{ color: "#0958d9" }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card
          size="small"
          loading={loading}
          className={`${styles.overallCard} ${styles.overallCardInfo}`}
          onClick={() => onMetricClick("all", "total_shipments")}
        >
          <Statistic
            title="Total Shipments"
            value={summary?.total_shipments ?? 0}
            valueStyle={{ color: "#0958d9" }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Tooltip title={reasonBreakdown(summary?.at_risk_reasons)}>
          <Card
            size="small"
            loading={loading}
            className={`${styles.overallCard} ${styles.overallCardAtRisk}`}
            onClick={() => onMetricClick("all", "total_at_risk")}
          >
            <Statistic
              title="At Risk"
              value={summary?.total_at_risk ?? 0}
              valueStyle={{ color: "#d46b08" }}
            />
          </Card>
        </Tooltip>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Tooltip title={reasonBreakdown(summary?.breach_reasons)}>
          <Card
            size="small"
            loading={loading}
            className={`${styles.overallCard} ${styles.overallCardBreached}`}
            onClick={() => onMetricClick("all", "total_breached")}
          >
            <Statistic
              title="Breached"
              value={summary?.total_breached ?? 0}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Tooltip>
      </Col>
    </Row>
  );

  // Per-tab refresh — reloads only the active board (load() is scoped to the current boardLane),
  // so refreshing Last Mile doesn't refetch First Mile and vice versa. Rendered inside each tab.
  const refreshBar = (
    <div className={styles.refreshBar}>
      {updatedAt && <span className={styles.updatedAt}>updated {dayjs(updatedAt).format("HH:mm:ss")}</span>}
      <Tooltip title="Refresh this board">
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <RadarChartOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Delivery Control Tower</h2>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load the Control Tower"
          description="What you see below may be out of date. Check your connection and try again."
          action={
            <Button size="small" danger onClick={load}>
              Try again
            </Button>
          }
        />
      )}

      {filterBar}

      <Tabs
        activeKey={boardLane}
        onChange={(k) => setBoardLane(k as ControlTowerBoardLane)}
        items={[
          {
            key: "last_mile",
            label: "Last Mile",
            children: (
              <>
                {refreshBar}
                {/* Totals across every lane, then the per-lane cards below. */}
                {overviewStrip(unclassifiedCount > 0 ? 8 : 20)}

                {unclassifiedCount > 0 && (
                  <div style={{ marginBottom: 20, color: "#ad6800", fontSize: 13 }}>
                    {unclassifiedCount} shipment{unclassifiedCount === 1 ? "" : "s"} in this scope
                    could not be classified into a lane (a client or vendor pincode didn't resolve) —
                    a data issue to chase, not a missing lane.
                  </div>
                )}

                {LANE_GROUPS.map((group) => (
                  <div key={group.title} className={styles.groupRow}>
                    <Tooltip title={group.help}>
                      <div className={styles.groupTitle}>
                        <span className={styles.groupIcon}>{group.icon}</span>
                        {group.title}
                      </div>
                    </Tooltip>
                    <div className={styles.laneGrid}>
                      {group.lanes.map((laneKey) => (
                        <LaneCard
                          key={laneKey}
                          laneKey={laneKey}
                          label={LANE_LABEL[laneKey]}
                          counts={summary?.lanes?.[laneKey] ?? EMPTY_LANE_COUNTS}
                          onMetricClick={onMetricClick}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {/* Per-owner performance for THIS board, below the lane groups. */}
                <Divider orientation="left" style={SECTION_DIVIDER_STYLE}>
                  Team Metric
                </Divider>
                <TeamMetricsSection
                  loading={loading}
                  members={teamMetrics.last_mile}
                  boardLane="last_mile"
                  onDrill={onTeamDrill}
                />
              </>
            ),
          },
          {
            key: "first_mile",
            label: "First Mile",
            children: (
              <>
                {refreshBar}

                {/* GRN and Picklist each render as either blocks or a per-warehouse table — the
                    section owns that toggle, and both views drill down through the same handler. */}
                <FirstMileGrnSection
                  loading={loading}
                  data={firstMileEscalations}
                  onDrill={(bucket, dock) => setEscalationDrilldown({ bucket, dock })}
                />

                <FirstMilePicklistSection
                  loading={loading}
                  data={picklistEscalations}
                  onDrill={(bucket, dock) => setPicklistEscalationDrilldown({ bucket, dock })}
                  onPickedClick={(dock) =>
                    setFirstMileDrilldown({ kind: "picklist", dock, metric: "total_shipments" })
                  }
                />

                {/* Inter-warehouse stock transfers on the Bengaluru↔Hyderabad line-haul. Two
                    direction sections, each with its receiving-warehouse cards; every count drills
                    down via kind=transfer. */}
                <Divider orientation="left" style={SECTION_DIVIDER_STYLE}>
                  Transfers
                </Divider>
                <TransferOrdersSection
                  loading={loading}
                  data={firstMileSummary?.transfer_orders}
                  onDrill={setTransferDrilldown}
                />

                {/* The same block as Last Mile, scoped to first-mile work — a person who owns both
                    appears on both tabs, each showing that lane's own figures. */}
                <Divider orientation="left" style={SECTION_DIVIDER_STYLE}>
                  Team Metric
                </Divider>
                <TeamMetricsSection
                  loading={loading}
                  members={teamMetrics.first_mile}
                  boardLane="first_mile"
                  onDrill={onTeamDrill}
                />
              </>
            ),
          },
        ]}
      />

      <ShipmentListDrawer
        request={drilldown}
        dateFrom={dateFrom}
        dateTo={dateTo}
        assignedTo={assignedTo}
        boardLane={boardLane}
        onClose={() => setDrilldown(null)}
      />

      <TeamEscalationDrawer
        request={teamEscalation}
        boardLane={boardLane}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onClose={() => setTeamEscalation(null)}
      />

      <FirstMileDrilldownDrawer
        request={firstMileDrilldown}
        dispatchFrom={dateFrom}
        dispatchTo={dateTo}
        onClose={() => setFirstMileDrilldown(null)}
      />

      <TransferOrdersDrawer
        request={transferDrilldown}
        dispatchFrom={dateFrom}
        dispatchTo={dateTo}
        assignedTo={assignedTo}
        onClose={() => setTransferDrilldown(null)}
      />

      <FirstMileEscalationDrawer
        request={escalationDrilldown}
        dispatchFrom={dateFrom}
        dispatchTo={dateTo}
        assignedTo={assignedTo}
        onClose={() => setEscalationDrilldown(null)}
      />

      <FirstMilePicklistEscalationDrawer
        request={picklistEscalationDrilldown}
        dispatchFrom={dateFrom}
        dispatchTo={dateTo}
        assignedTo={assignedTo}
        onClose={() => setPicklistEscalationDrilldown(null)}
      />
    </div>
  );
};

export default ControlTower;
